import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import {
    ArrowLeft,
    Package,
    Upload01,
    CheckCircle,
    XCircle,
    Trash01
} from '@untitledui/icons';
import { FileIcon as FileTypeIcon } from '@untitledui/file-icons';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { Select } from '@/components/base/select/select';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { FileUpload } from '@/components/application/file-upload/file-upload-base';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useShipmentById } from '@/hooks/api/use-containers-recap';
import { useDocumentTypes } from '@/hooks/api/use-document-types';
import { storage } from '@/lib/local-storage';

interface UploadFile {
    file: File;
    id: string;
    type: string;
    preview?: string;
    progress: number;
    status: 'pending' | 'uploading' | 'completed' | 'error';
    error?: string;
}

const ACCEPTED_FORMATS = '.pdf,.xlsx,.xls,.csv,.docx,.doc,.jpg,.jpeg,.png,.gif,.webp,.zip';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const detectDocumentType = (fileName: string): string => {
    const name = fileName.toLowerCase();

    if (name.includes('bill') || name.includes('lading') || name.includes('b/l')) {
        return 'bill_of_lading';
    }
    if (name.includes('invoice') || name.includes('fattura')) {
        return 'commercial_invoice';
    }
    if (name.includes('packing') || name.includes('list')) {
        return 'packing_list';
    }
    if (name.includes('phyto') || name.includes('sanitary')) {
        return 'phytosanitary_certificate';
    }
    if (name.includes('movement') || name.includes('eur')) {
        return 'movement_certificate';
    }
    if (name.includes('fumigation')) {
        return 'fumigation_certificate';
    }
    if (name.includes('sad') || name.includes('icm')) {
        return 'sad';
    }
    if (name.includes('coo') || name.includes('origin')) {
        return 'certificate_of_origin';
    }

    return '';
};

const getFileIconType = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
        case 'pdf':
            return 'pdf';
        case 'xlsx':
            return 'xlsx';
        case 'xls':
            return 'xls';
        case 'csv':
            return 'csv';
        case 'docx':
            return 'docx';
        case 'doc':
            return 'doc';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'webp':
            return 'image';
        case 'zip':
            return 'zip';
        case 'rar':
            return 'rar';
        case '7z':
            return '7z';
        case 'txt':
            return 'txt';
        case 'rtf':
            return 'rtf';
        case 'ppt':
        case 'pptx':
            return 'powerpoint';
        default:
            return 'generic';
    }
};

const getReadableFileSize = (bytes: number) => {
    if (bytes === 0) return "0 KB";
    const suffixes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.floor(bytes / Math.pow(1024, i)) + " " + suffixes[i];
};

export const ContainerUploadPage = () => {
    const { t, i18n } = useTranslation(['pages:containers', 'pages:documents', 'types', 'common']);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [uploadedFiles, setUploadedFiles] = useState<UploadFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [filesToUpload, setFilesToUpload] = useState<UploadFile[]>([]);

    // Fetch container data from real API
    const { data: shipment, isLoading } = useShipmentById(id);

    // Fetch document types dynamically
    const { data: documentTypes } = useDocumentTypes();

    const documentTypeOptions = [
        { id: '', label: t('pages:documents:drive.upload.preview.unknownDocument', { defaultValue: 'Unknown' }) },
        ...(documentTypes?.map((dt) => {
            const localizedName = i18n.language === 'it' ? dt.name_it : i18n.language === 'en' ? dt.name_en : dt.name;
            return {
                id: dt.key,
                label: localizedName || dt.name,
            };
        }) || []),
    ];

    // Parse error response from backend into a readable message
    const parseErrorResponse = (responseText: string): string => {
        try {
            const json = JSON.parse(responseText);
            if (typeof json.detail === 'string') return json.detail;
            if (Array.isArray(json.detail)) {
                return json.detail.map((e: { msg?: string; loc?: string[] }) => {
                    const field = e.loc?.slice(-1)[0] || '';
                    return field ? `${field}: ${e.msg}` : (e.msg || 'Validation error');
                }).join('; ');
            }
            return 'Upload failed';
        } catch {
            return responseText || 'Upload failed';
        }
    };

    // Upload a single file via XHR with progress tracking
    const uploadFileWithProgress = (uploadFile: UploadFile, containerId: string): Promise<void> => {
        const formData = new FormData();
        formData.append('file', uploadFile.file);
        formData.append('container_id', containerId);

        // Send document_type_id (UUID) if available, fallback to document_type (key)
        if (uploadFile.type && documentTypes) {
            const dt = documentTypes.find(d => d.key === uploadFile.type);
            if (dt?.document_type_id) {
                formData.append('document_type_id', dt.document_type_id);
            } else {
                formData.append('document_type', uploadFile.type);
            }
        }

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const progress = Math.round((e.loaded / e.total) * 100);
                    setUploadedFiles((prev) =>
                        prev.map((f) => f.id === uploadFile.id ? { ...f, progress } : f)
                    );
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error(parseErrorResponse(xhr.responseText)));
                }
            });

            xhr.addEventListener('error', () => reject(new Error('Network error')));

            const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
            const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || '/api/v1';
            xhr.open('POST', `${baseUrl}${versionPrefix}/documents/upload`);

            const token = storage.getItem('access_token');
            if (token) {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }

            xhr.send(formData);
        });
    };

    const handleDropFiles = (files: FileList) => {
        const newFiles: UploadFile[] = [];

        Array.from(files).forEach((file) => {
            if (file.size > MAX_FILE_SIZE) {
                alert(t('upload.fileTooLarge', { fileName: file.name }));
                return;
            }

            const uploadFile: UploadFile = {
                file,
                id: Math.random().toString(36).substring(2, 11),
                type: detectDocumentType(file.name),
                progress: 0,
                status: 'pending'
            };

            // Create preview for images
            if (file.type.startsWith('image/')) {
                uploadFile.preview = URL.createObjectURL(file);
            }

            newFiles.push(uploadFile);
        });

        setUploadedFiles(prev => [...prev, ...newFiles]);
    };

    const handleRemoveFile = (fileId: string) => {
        setUploadedFiles(prev => {
            const file = prev.find(f => f.id === fileId);
            if (file?.preview) {
                URL.revokeObjectURL(file.preview);
            }
            return prev.filter(f => f.id !== fileId);
        });
    };

    const handleTypeChange = (fileId: string, newType: string) => {
        setUploadedFiles(prev =>
            prev.map(f => f.id === fileId ? { ...f, type: newType } : f)
        );
    };

    const handleUpload = async () => {
        if (uploadedFiles.length === 0) {
            alert(t('upload.selectAtLeastOne'));
            return;
        }

        const filesWithoutType = uploadedFiles.filter(f => !f.type);
        if (filesWithoutType.length > 0) {
            setFilesToUpload(uploadedFiles);
            setShowConfirmModal(true);
            return;
        }

        await performUpload(uploadedFiles);
    };

    const performUpload = async (files: UploadFile[]) => {
        if (!id) return;
        setIsUploading(true);

        for (const uploadFile of files) {
            // Update status to uploading
            setUploadedFiles(prev =>
                prev.map(f => f.id === uploadFile.id ? { ...f, status: 'uploading' } : f)
            );

            try {
                await uploadFileWithProgress(uploadFile, id);

                // Update status to completed
                setUploadedFiles(prev =>
                    prev.map(f => f.id === uploadFile.id ? { ...f, status: 'completed', progress: 100 } : f)
                );
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Upload failed';
                setUploadedFiles(prev =>
                    prev.map(f => f.id === uploadFile.id ? { ...f, status: 'error', error: errorMessage } : f)
                );
            }
        }

        setIsUploading(false);
        // Only show success screen if at least one file uploaded successfully
        setUploadedFiles(prev => {
            const hasSuccess = prev.some(f => f.status === 'completed');
            if (hasSuccess) {
                setUploadComplete(true);
                // Invalidate document queries so the container detail page shows new docs
                if (id) {
                    queryClient.invalidateQueries({ queryKey: ['documents', 'container', id] });
                    queryClient.invalidateQueries({ queryKey: ['documents', 'list'] });
                }
            }
            return prev;
        });
    };

    const handleConfirmUpload = async () => {
        setShowConfirmModal(false);
        await performUpload(filesToUpload);
    };

    const handleCancelUpload = () => {
        setShowConfirmModal(false);
        setFilesToUpload([]);
    };

    const handleReturnToDocuments = () => {
        navigate(`/shipper/containers/${id}?tab=documents`);
    };

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            uploadedFiles.forEach(file => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('upload.loading')}</p>
            </div>
        );
    }

    if (!shipment) {
        return (
            <div className="text-center py-12">
                <Package className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">{t('upload.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('upload.notFound.message')}</p>
                <Link
                    to="/shipper/containers"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <ArrowLeft className="size-5" />
                    {t('upload.notFound.backToList')}
                </Link>
            </div>
        );
    }

    const containerLabel = shipment.container_number || shipment.booking_number || '—';
    const routeLabel = [shipment.origin_port_name, shipment.destination_port_name].filter(Boolean).join(' → ');

    if (uploadComplete) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Link
                        to={`/shipper/containers/${id}`}
                        className="text-tertiary hover:text-primary"
                    >
                        <ArrowLeft className="size-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('upload.uploadCompleted')}</h1>
                        <p className="text-tertiary mt-1">
                            {containerLabel} - {routeLabel}
                        </p>
                    </div>
                </div>

                {/* Success Message */}
                <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                    <div className="flex justify-center mb-4">
                        <div className="rounded-full bg-success-100 p-3">
                            <CheckCircle className="size-8 text-success-600" />
                        </div>
                    </div>
                    <h2 className="text-xl font-semibold text-primary mb-2">
                        {t('upload.documentsUploadedSuccess')}
                    </h2>
                    <p className="text-tertiary mb-6">
                        {uploadedFiles.length} {t('upload.documentsNowAvailable')}
                    </p>
                    <Button
                        size="lg"
                        color="primary"
                        onClick={handleReturnToDocuments}
                        className="inline-flex items-center gap-2"
                        iconLeading={Package}
                    >
                        {t('upload.goToDocuments')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Link
                    to={`/shipper/containers/${id}`}
                    className="text-tertiary hover:text-primary"
                >
                    <ArrowLeft className="size-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('upload.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {containerLabel} - {routeLabel}
                    </p>
                </div>
            </div>

            {/* Container Info */}
            <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Package className="size-5 text-brand-600" />
                        <div>
                            <div className="text-sm font-medium text-primary">{t('upload.container')}</div>
                            <div className="text-sm text-tertiary">{containerLabel}</div>
                        </div>
                    </div>
                    <Badge color="brand" size="sm">
                        {shipment.status}
                    </Badge>
                </div>
            </div>

            {/* File Upload */}
            <div className="space-y-4">
                <h2 className="text-lg font-semibold text-primary">{t('upload.selectFiles')}</h2>
                <FileUpload.Root>
                    <FileUpload.DropZone
                        accept={ACCEPTED_FORMATS}
                        allowsMultiple={true}
                        maxSize={MAX_FILE_SIZE}
                        onDropFiles={handleDropFiles}
                        hint={`PDF, Excel, CSV, Word, Immagini, ZIP (max. 10MB per file)`}
                    />
                </FileUpload.Root>
            </div>

            {/* File List */}
            {uploadedFiles.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-primary">{t('upload.selectedFiles')}</h2>
                        <span className="text-sm text-tertiary">{uploadedFiles.length} {t('upload.file')}</span>
                    </div>

                    <div className="space-y-3">
                        {uploadedFiles.map((uploadFile) => (
                            <div
                                key={uploadFile.id}
                                className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary"
                            >
                                <div className="flex items-start gap-4">
                                    {/* File Preview/Icon */}
                                    <div className="flex-shrink-0">
                                        {uploadFile.preview ? (
                                            <img
                                                src={uploadFile.preview}
                                                alt={uploadFile.file.name}
                                                className="size-12 rounded-lg object-cover"
                                            />
                                        ) : (
                                            <div className="size-12 rounded-lg bg-secondary flex items-center justify-center">
                                                <FileTypeIcon
                                                    className="size-6 text-tertiary"
                                                    type={getFileIconType(uploadFile.file.name)}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* File Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between">
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-sm font-medium text-primary truncate">
                                                    {uploadFile.file.name}
                                                </h3>
                                                <p className="text-sm text-tertiary">
                                                    {getReadableFileSize(uploadFile.file.size)}
                                                </p>
                                            </div>

                                            {uploadFile.status === 'pending' && (
                                                <button
                                                    onClick={() => handleRemoveFile(uploadFile.id)}
                                                    className="text-tertiary hover:text-error-600 ml-2"
                                                >
                                                    <Trash01 className="size-4" />
                                                </button>
                                            )}
                                        </div>

                                        {/* Document Type Selection */}
                                        {uploadFile.status === 'pending' && (
                                            <div className="mt-3">
                                                <Select.ComboBox
                                                    selectedKey={uploadFile.type}
                                                    onSelectionChange={(key) =>
                                                        handleTypeChange(uploadFile.id, key as string)
                                                    }
                                                    items={documentTypeOptions}
                                                    placeholder={t('pages:documents:drive.upload.preview.searchDocumentType', { defaultValue: 'Search document type...' })}
                                                    shortcut={false}
                                                    size="sm"
                                                >
                                                    {(item) => (
                                                        <Select.Item id={item.id} textValue={item.label}>
                                                            {item.label}
                                                        </Select.Item>
                                                    )}
                                                </Select.ComboBox>
                                            </div>
                                        )}

                                        {/* Upload Progress */}
                                        {uploadFile.status === 'uploading' && (
                                            <div className="mt-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Upload01 className="size-4 text-brand-600" />
                                                    <span className="text-sm text-tertiary">{t('upload.uploading')}</span>
                                                    <span className="text-sm text-primary">{uploadFile.progress}%</span>
                                                </div>
                                                <ProgressBar
                                                    value={uploadFile.progress}
                                                    min={0}
                                                    max={100}
                                                />
                                            </div>
                                        )}

                                        {/* Completed Status */}
                                        {uploadFile.status === 'completed' && (
                                            <div className="mt-3 flex items-center gap-2">
                                                <CheckCircle className="size-4 text-success-600" />
                                                <span className="text-sm text-success-600 font-medium">
                                                    {t('upload.uploadedSuccessfully')}
                                                </span>
                                            </div>
                                        )}

                                        {/* Error Status */}
                                        {uploadFile.status === 'error' && (
                                            <div className="mt-3 flex items-center gap-2">
                                                <XCircle className="size-4 text-error-primary" />
                                                <span className="text-sm text-error-primary font-medium">
                                                    {uploadFile.error || t('upload.uploadFailed', { defaultValue: 'Upload failed' })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Upload Button */}
                    {!isUploading && (
                        <div className="flex justify-end">
                            <Button
                                size="lg"
                                color="primary"
                                onClick={handleUpload}
                                className="inline-flex items-center gap-2"
                                iconLeading={Upload01}
                            >
                                {t('upload.uploadSelected')}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Confirm Upload Modal */}
            {showConfirmModal && (
                <ModalOverlay isOpen={showConfirmModal} onOpenChange={setShowConfirmModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="rounded-full bg-warning-100 p-2">
                                        <XCircle className="size-5 text-warning-600" />
                                    </div>
                                    <h2 className="text-lg font-semibold text-primary">
                                        {t('upload.confirmUploadTitle')}
                                    </h2>
                                </div>

                                <div className="mb-6">
                                    <p className="text-sm text-tertiary mb-3">
                                        {t('upload.unknownTypeWarning')}
                                    </p>

                                    <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                                        <p className="text-sm text-warning-700 font-medium mb-2">
                                            {t('upload.unknownTypeDocuments')}
                                        </p>
                                        <ul className="text-sm text-warning-600 space-y-1">
                                            {filesToUpload.filter(f => !f.type).map(file => (
                                                <li key={file.id} className="flex items-center gap-2">
                                                    <span className="w-1 h-1 bg-warning-600 rounded-full"></span>
                                                    {file.file.name}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <p className="text-sm text-tertiary mt-3">
                                        {t('upload.proceedAnyway')}
                                    </p>
                                </div>

                                <div className="flex gap-3 justify-end">
                                    <Button
                                        size="md"
                                        color="secondary"
                                        onClick={handleCancelUpload}
                                    >
                                        {t('upload.cancel')}
                                    </Button>
                                    <Button
                                        size="md"
                                        color="primary"
                                        onClick={handleConfirmUpload}
                                        className="inline-flex items-center gap-2"
                                        iconLeading={Upload01}
                                    >
                                        {t('upload.proceedWithUpload')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};
